import { JoinCampaignResponse, JoinCampaignErrorCode, JOIN_CAMPAIGN_ERROR_CODE, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys, getHoldParticipantStatusQueryKey } from "@cede/utils";
import { useHubUser } from "../user";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

export const MERKL_EVM_WALLET_REQUIRED_MESSAGE =
  "Merkl pays EVM wallets only. Sign in with an EVM wallet to join this campaign.";

interface JoinCampaignError extends Error {
  code: JoinCampaignErrorCode;
}

export const useJoinHoldCampaign = () => {
  const queryClient = useQueryClient();
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const { trackEvent } = useMetricsEvents();

  return useMutation<JoinCampaignResponse, JoinCampaignError, { campaignId: string; campaignName: string }>({
    mutationFn: async ({ campaignId }) => {
      const response = await apiHelper.postAuth(auth, API_ENDPOINT.HOLD_TO_EARN_JOIN_CAMPAIGN, {
        bodyParams: { campaignId },
      });

      // Check if the response indicates failure and throw an error for onError handler
      if (!response.success) {
        const error = new Error(response.message) as JoinCampaignError;
        error.code = ((response as { code?: string }).code || "UNKNOWN_ERROR") as JoinCampaignErrorCode;
        throw error;
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch queries to get fresh data from API
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HOLD_TO_EARN_CAMPAIGNS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HOLD_TO_EARN_CAMPAIGN] });
      queryClient.invalidateQueries({
        queryKey: getHoldParticipantStatusQueryKey(variables.campaignId, hubUser?.id),
      });

      // Track successful hold campaign join
      trackEvent(METRICS_EVENTS.JOIN_HOLD_CAMPAIGN_SUCCESS, {
        campaignId: variables.campaignId,
        campaignName: variables.campaignName,
        campaignType: "hold-to-earn",
      });
      enqueueSnackbar("Successfully joined the campaign!", { variant: "success" });
    },
    onError: (error: JoinCampaignError, variables) => {
      // eslint-disable-next-line no-console
      console.error("Error joining campaign", error);

      // Track failed hold campaign join with error details
      trackEvent(METRICS_EVENTS.JOIN_HOLD_CAMPAIGN_FAILED, {
        campaignId: variables.campaignId,
        campaignName: variables.campaignName,
        campaignType: "hold-to-earn",
        errorCode: "code" in error ? error.code : undefined,
        errorMessage: error.message,
      });
      enqueueSnackbar(
        error.code === JOIN_CAMPAIGN_ERROR_CODE.EVM_WALLET_REQUIRED ? MERKL_EVM_WALLET_REQUIRED_MESSAGE : error.message,
        { variant: "error" },
      );
    },
  });
};
